import { Component, EventEmitter, Input, Output } from '@angular/core';
import { isBetaIntegration } from '../../models/integration/beta-integrations.constant';
import { IntegrationCardView } from '../../models/integration/vendor-definition.model';

/**
 * Universal integration card. Identical on every page (Org Settings + Company)
 * and for every card-shaped integration surface (vendors, partner apps,
 * outbound API credentials).
 *
 * Pure presentation: it does NOT know which context it lives in. The parent
 * computes `connected` / `needsAction` per context and passes them in. The only
 * action is a single "Info" button — all state-based actions (connect, disconnect,
 * manage, sync) live inside the modal the parent opens on `infoClick`.
 *
 * Badge at a fixed position:
 *   - `badgeKey` set → that text with `badgeVariant` colors (overrides the booleans)
 *   - `needsAction` → orange "Aktion erforderlich" (takes precedence over connected)
 *   - else `connected` → green "Verbunden"
 *   - else no badge
 *
 * Independent of that status badge, an integration still in validation carries a small "BETA"
 * pill next to its name, with the explanation as a tooltip (`BETA_INTEGRATIONS`, REV-22557).
 */
@Component({
    selector: 'aumico-vendor-integration-card',
    templateUrl: 'integration-card.component.html',
    styleUrls: ['integration-card.component.scss'],
})
export class IntegrationCardComponent {
    @Input() public vendor!: IntegrationCardView;

    /** True when this vendor is connected/bound in the parent's context. Drives the green badge. */
    @Input() public connected = false;

    /**
     * True when something needs the user's attention (e.g. an org connection is
     * EXPIRED/ERROR, or a company's mapped connection became unhealthy). Shows the
     * orange "Aktion erforderlich" badge in place of the green one.
     */
    @Input() public needsAction = false;

    /** Generic badge override (i18n key) for card types outside connected/needsAction semantics. */
    @Input() public badgeKey: string | null = null;

    @Input() public badgeVariant: 'connected' | 'action' | 'danger' = 'connected';

    @Output() public infoClick = new EventEmitter<void>();

    /** True while this integration is still being validated with early customers. */
    public get beta(): boolean {
        return isBetaIntegration(this.vendor?.vendor);
    }

    public onInfo(): void {
        this.infoClick.emit();
    }
}
